import { Dispatch, SetStateAction, useMemo, useState } from "react";
import CircleAvatar from "../../../../../components/CircleAvatar/CircleAvatar";
import Input from "../../../../../components/Input/Input";
import Loader from "../../../../../components/Loader/Loader";
import ModalWindow from "../../../../../components/ModalWindow/ModalWindow";
import MultipleSelect from "../../../../../components/MultipleSelect/MultipleSelect";
import {
  HubInvitationStatus,
  ProjectTeamInvitation,
} from "../../../../../types/Projects";
import { formatDateAndTime } from "../../../../../utils/dateActions";
import { getTranslation } from "../../../../../utils/getTranslation";
import inviteS from "./InviteStatistic/InviteStatistic.module.css";
import { formatTeamRole, teamRoleOptions } from "./programInfoUtils";
import s from "./ProgramInfo.module.css";

type TeamInvitationsModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  invitations: ProjectTeamInvitation[];
  isLoading: boolean;
};

const getUserName = (invite: ProjectTeamInvitation) =>
  `${getTranslation(invite.invitedUser?.name, "en")} ${getTranslation(
    invite.invitedUser?.surname,
    "en",
  )}`.trim() ||
  invite.invitedUser?.email ||
  "Invited user";

const getInvitedByName = (invite: ProjectTeamInvitation) =>
  `${getTranslation(invite.invitedBy?.name, "en")} ${getTranslation(
    invite.invitedBy?.surname,
    "en",
  )}`.trim() ||
  invite.invitedBy?.email ||
  "-";

const getStatus = (status: HubInvitationStatus) => {
  if (status === HubInvitationStatus.ACCEPTED) {
    return {
      className: inviteS.acceptStatus,
      title: "Accepted",
    };
  }

  if (status === HubInvitationStatus.DECLINED) {
    return {
      className: inviteS.rejectStatus,
      title: "Declined",
    };
  }

  return {
    className: inviteS.awaitStatus,
    title: "Pending",
  };
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const formatted = formatDateAndTime(date, "en");
  return `${formatted.formattedDate}, ${formatted.formattedTime}`;
};

const statusFilterData = [
  { lable: "All", value: "All" },
  { lable: "Accepted", value: "Accepted" },
  { lable: "Declined", value: "Declined" },
  { lable: "Pending", value: "Pending" },
];

const roleFilterData = [{ lable: "All roles", value: "All" }, ...teamRoleOptions];

const TeamInvitationsModal = ({
  isOpen,
  setIsOpen,
  invitations,
  isLoading,
}: TeamInvitationsModalProps) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");

  const filteredInvitations = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return invitations
      .filter((invite) => {
        const name = getUserName(invite).toLowerCase();
        const email = (invite.invitedUser?.email ?? "").toLowerCase();
        const invitedBy = getInvitedByName(invite).toLowerCase();

        const matchesSearch =
          !normalizedSearch ||
          name.includes(normalizedSearch) ||
          email.includes(normalizedSearch) ||
          invitedBy.includes(normalizedSearch);

        const status = getStatus(invite.status).title;
        const matchesStatus =
          statusFilter === "All" || status === statusFilter;

        const matchesRole =
          roleFilter === "All" || invite.roles.includes(roleFilter);

        return matchesSearch && matchesStatus && matchesRole;
      })
      .sort(
        (a, b) =>
          new Date(b.invitedAt).getTime() - new Date(a.invitedAt).getTime(),
      );
  }, [invitations, roleFilter, search, statusFilter]);

  return (
    <ModalWindow
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      width="fit-content"
      padding="20px"
      title="Project team invites"
    >
      {isLoading ? (
        <div className={s.teamModalLoader}>
          <Loader size={80} />
        </div>
      ) : (
        <div className={inviteS.container}>
          <span className={inviteS.filtersTitle}>Filters</span>
          <div className={inviteS.filters}>
            <Input
              inputValue={search}
              onChangeInput={setSearch}
              isVisible
              required
              placeholder="Search by name, email or inviter"
              width="25%"
            />
            <MultipleSelect
              data={statusFilterData}
              multiplie={false}
              setValue={(value) => setStatusFilter(value as string)}
              value={statusFilter}
              disableSortData
              width="25%"
            />
            <MultipleSelect
              data={roleFilterData}
              multiplie={false}
              setValue={(value) => setRoleFilter(value as string)}
              value={roleFilter}
              disableSortData
              width="25%"
            />
          </div>

          <div className={inviteS.tableBlock}>
            <table className={inviteS.table}>
              <thead>
                <tr className={inviteS.headerRow}>
                  <th>Name</th>
                  <th>Roles</th>
                  <th>Email</th>
                  <th>Invited By</th>
                  <th>Sent Date</th>
                  <th>Answer Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvitations.length ? (
                  filteredInvitations.map((invite) => (
                    <tr key={invite._id} className={inviteS.row}>
                      <td>
                        <div className={inviteS.nameAvatarBlock}>
                          <CircleAvatar userId={invite.invitedUser._id} />
                          <span>{getUserName(invite)}</span>
                        </div>
                      </td>
                      <td>
                        {invite.roles.length
                          ? invite.roles.map((role) => formatTeamRole(role)).join(", ")
                          : "-"}
                      </td>
                      <td>{invite.invitedUser?.email || "-"}</td>
                      <td>{getInvitedByName(invite)}</td>
                      <td>{formatDateTime(invite.invitedAt)}</td>
                      <td>{formatDateTime(invite.respondedAt)}</td>
                      <td>
                        <div
                          className={`${inviteS.status} ${
                            getStatus(invite.status).className
                          }`}
                        >
                          {getStatus(invite.status).title}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className={inviteS.emptyState}>
                      Invitations not found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ModalWindow>
  );
};

export default TeamInvitationsModal;
